import { forwardRef, Suspense, lazy, useImperativeHandle, useRef } from 'react';
import { BRAND } from '@/config/brand';
import { MapMiniCard } from './MapMiniCard';

const LeafletMap = lazy(() => import('./LeafletMap'));
const GoogleMapView = lazy(() => import('./GoogleMapView'));

const MAP_FALLBACK = (
  <div className="grid h-full w-full place-items-center bg-slate-100 text-sm text-slate-500">
    Loading map…
  </div>
);

export const MapView = forwardRef(function MapView({
  listings = [],
  activeId,
  hoveredId,
  selectedListing,
  filters,
  onSelect,
  onCloseCard,
  onBoundsChange,
  searchOnMove,
  drawMode,
  onPolygonComplete,
  drawnPolygon,
  onDrawModeChange,
  onSearchArea,
  onClearDrawnPolygon,
  focusKey,
  autoLocate,
  onAutoLocation,
}, ref) {
  const hasGoogleKey = Boolean(BRAND.googleMapsApiKey);
  const engineRef = useRef(null);

  useImperativeHandle(ref, () => ({
    clearPolygon: () => engineRef.current?.clearPolygon(),
    zoomTo: (lat, lng, zoom) => engineRef.current?.zoomTo(lat, lng, zoom),
    fitBounds: (bounds) => engineRef.current?.fitBounds(bounds),
    setBoundaryCameraMode: (mode) => engineRef.current?.setBoundaryCameraMode?.(mode),
    paintBoundary: (geojson, bbox, opts) => engineRef.current?.paintBoundary?.(geojson, bbox, opts),
  }));

  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="map-view">
      <Suspense fallback={MAP_FALLBACK}>
        {hasGoogleKey ? (
          <GoogleMapView
            ref={engineRef}
            listings={listings}
            activeId={activeId}
            hoveredId={hoveredId}
            filters={filters}
            onSelect={onSelect}
            onCloseCard={onCloseCard}
            onBoundsChange={onBoundsChange}
            searchOnMove={searchOnMove}
            drawMode={drawMode}
            onPolygonComplete={onPolygonComplete}
            drawnPolygon={drawnPolygon}
            onDrawModeChange={onDrawModeChange}
            onSearchArea={onSearchArea}
            onClearDrawnPolygon={onClearDrawnPolygon}
            focusKey={focusKey}
            autoLocate={autoLocate}
            onAutoLocation={onAutoLocation}
          />
        ) : (
          <LeafletMap
            listings={listings}
            activeId={activeId}
            hoveredId={hoveredId}
            onSelect={onSelect}
            onBoundsChange={onBoundsChange}
            searchOnMove={searchOnMove}
          />
        )}
      </Suspense>
      {!hasGoogleKey && (
        <div className="pointer-events-none absolute right-2 top-2 z-[500] rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-sm">
          Leaflet preview · add Google key to enable Google Maps
        </div>
      )}
      {/* Google desktop uses the list column; on mobile that column is hidden, so show the card.
          Leaflet has no list column integration — always show. */}
      <div className={hasGoogleKey ? 'md:hidden' : undefined}>
        <MapMiniCard
          listing={selectedListing}
          onClose={onCloseCard}
          onZoomHere={undefined}
        />
      </div>
    </div>
  );
});
